import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Activity, Heart, Loader2, Mail } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

function timeAgo(iso) {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export const ReplyInsightsCard = () => {
  const { company } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [thanking, setThanking] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!company) return;
    api.get(`/companies/${company.id}/impact/recent-events?limit=8`)
      .then((r) => setEvents(r.data.events || []))
      .catch((e) => console.error("engagement feed failed", e));
  }, [company]);

  const openThank = (ev) => {
    setThanking(ev);
    setMessage(`Thanks so much for this, ${(ev.investor_name || "there").split(" ")[0]} - it genuinely moved the needle for us.`);
  };

  const sendThanks = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/impact/${thanking.investor_id}/thank`, { message });
      toast.success(data.email_status === "sent" ? `Thank-you emailed to ${thanking.investor_name}` : "Thank-you sent");
      setThanking(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send thanks"); }
    finally { setSending(false); }
  };

  return (
    <Card className="rounded-xl border bg-card p-5" data-testid="engagement-feed">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Activity className="h-4 w-4 text-primary" /> Investor engagement feed
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate("/app/investor-impact")} data-testid="engagement-feed-view-impact">View impact</Button>
      </div>
      {events.length === 0 ? (
        <p className="py-6 text-center text-xs text-muted-foreground" data-testid="engagement-feed-empty">
          No interactions logged yet. Auto-logged investor replies (inbound email) and manually logged actions will appear here in real time.
        </p>
      ) : (
        <div className="space-y-2.5">
          {events.map((ev) => (
            <div key={ev.id} className="group flex items-center gap-3" data-testid={`engagement-event-${ev.id}`}>
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${ev.category === "disruptive" ? "bg-red-500" : "bg-emerald-500"}`} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-medium text-foreground">
                  {ev.investor_name || ev.investor_email} · <span className="font-normal text-muted-foreground">{ev.label}</span>
                </div>
                {ev.note && <div className="truncate text-[11px] text-muted-foreground/80">{ev.note}</div>}
              </div>
              {ev.source === "email" && (
                        <Badge variant="outline" className="gap-1 border-brand/30 bg-brand/10 px-1.5 py-0 text-[10px] text-brand-ink"><Mail className="h-2.5 w-2.5" />Auto</Badge>
              )}
              <span className={`text-[11px] font-semibold tabular-nums ${ev.points < 0 ? "text-red-600" : "text-emerald-600"}`}>
                {ev.points > 0 ? `+${ev.points}` : ev.points}
              </span>
              <span className="w-12 text-right text-[11px] text-muted-foreground">{timeAgo(ev.created_at)}</span>
              <button onClick={() => openThank(ev)} title="Say thanks" className="text-muted-foreground transition-colors hover:text-rose-500" data-testid={`thank-btn-${ev.id}`}>
                <Heart className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!thanking} onOpenChange={(o) => { if (!o) setThanking(null); }}>
        <DialogContent className="max-w-md" data-testid="thank-dialog">
          <DialogHeader>
            <DialogTitle>Thank {thanking?.investor_name}</DialogTitle>
            <DialogDescription>Sends a short branded email plus an in-app note. Small thanks keep helpful investors helping.</DialogDescription>
          </DialogHeader>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className="text-sm" data-testid="thank-message" />
          <Button onClick={sendThanks} disabled={sending || !message.trim()} className="w-full gap-2" data-testid="thank-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" />} Send thanks
          </Button>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
